import { Treatment } from '@App/services/api/TreatmentsService/types/Treatment';
import { useTreatments } from '@Framework/hooks/useTreatments';
import {
  Box,
  Dialog,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { CORPORATE_COLORS } from '@Ui/theme/colors/corporate';
import React from 'react';

import { BasicSelectProps } from './BasicSelect.types';

const useStyles = makeStyles(theme => ({
  textFieldFormControl: {
    backgroundColor: 'transparent',
    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: CORPORATE_COLORS.RED
    }
  },
  iconAbTest: {
    background: '#34cb9e',
    fill: 'white',
    width: '3.5rem',
    height: '100%',
    top: '0',
    right: '0',
    padding: '10px'
  },
  modal: {
    backgroundColor: CORPORATE_COLORS.WHITE,
    padding: '0px',
    paddingTop: '0px !important',
    marginTop: '20px'
  },
  menuPaper: {
    top: '129px !important',
    maxHeight: '40rem',
    zIndex: 1400,
    boxShadow: 'none',
    border: '1px solid',
    borderTop: 0,
    borderColor: CORPORATE_COLORS.BACKGROUND_LIGHT_PRIMARY_HIGH
  },
  menuPaperAbTest: {
    maxHeight: '40rem',
    zIndex: 1400,
    marginTop: '3.5rem'
  },
  menuItems: {
    fontSize: '14px',
    padding: '12px 24px',
    minHeight: '21px',
    lineHeight: '21px',
    display: 'grid',
    gridTemplateColumns: '100% 0%',
    '&:hover': {
      backgroundColor: CORPORATE_COLORS.BACKGROUND_PRIMARY_LOW
    }
  },
  menuItemsError: {
    color: CORPORATE_COLORS.RED
  },
  iconStyle: {
    color: CORPORATE_COLORS.POSITIVE,
    height: '19px'
  },
  closeIconStyle: {
    color: CORPORATE_COLORS.PRIMARY,
    height: '30px',
    width: '30px'
  },
  headline: {
    color: CORPORATE_COLORS.DARK_GRAY
  },
  nativeInput: {
    backgroundColor: 'transparent',
    fontSize: '16px',
    bottom: 'auto',
    border: 'none',
    paddingTop: theme.spacing(2.5),
    paddingLeft: theme.spacing(3),
    [theme.breakpoints.up('md')]: {
      opacity: 'unset'
    }
  },
  selectedIndication: {
    backgroundColor: CORPORATE_COLORS.BACKGROUND_PRIMARY_LOW
  },
  errorFormHelper: {
    backgroundColor: 'transparent',
    color: CORPORATE_COLORS.RED
  },
  selectRoot: {
    '&[aria-expanded="true"]': {
      backgroundColor: CORPORATE_COLORS.BACKGROUND_PRIMARY_LOW
    },
    '&.Mui-error': {
      color: CORPORATE_COLORS.RED
    }
  },
  bottomBorder: {
    padding: '5px',
    borderBottom: '1px solid rgb(153 153 153)',
    width: '100%'
  },
  stickyTopItems: {
    position: 'sticky',
    paddingTop: 0,
    top: 0,
    zIndex: 100,
    backgroundColor: '#fff'
  },
  webkitSticky: {
    position: '-webkit-sticky'
  }
}));

interface SelectProps extends BasicSelectProps {
  required?: boolean;
  error?: boolean;
  onBlur?: any;
  errorText?: string;
  onOpen?: () => void;
  treatments: Treatment[];
}

const BasicSelect = ({
  label,
  value,
  id,
  name,
  onChange = () => undefined,
  style,
  className,
  required,
  error,
  errorText,
  onOpen,
  onBlur,
  treatments
}: SelectProps): React.ReactElement => {
  const classes = useStyles();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [open, setOpen] = React.useState(false);

  const handleDialogMenuItemClick = (value: string) => {
    onChange(value);
  };

  const handleClose = () => {
    setOpen(false);
    document.body.style['height'] = '';
    document.body.style['width'] = '';
  };

  const handleOpen = () => {
    document.body.style['height'] = '100vh';
    document.body.style['width'] = '100vw';
    setOpen(true);
    onOpen && onOpen();
  };

  const renderMobileMenuItems = (treatments: Treatment[]) => {
    return treatments.map(({ text, id }, index) => (
      <MenuItem
        selected={value === text}
        onClick={() => handleDialogMenuItemClick(text)}
        classes={{
          root: classes.menuItems,
          selected: classes.selectedIndication
        }}
        key={'mobile_' + id + '_' + index}
        value={text}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '90% 10%' }}>
          <div>{text}</div>
          {value === text && (
            <div>
              <CheckIcon className={classes.iconStyle} />
            </div>
          )}
        </div>
      </MenuItem>
    ));
  };

  const renderDesktopMenuItems = (treatments: Treatment[]) => {
    return treatments.map(({ text }, index) => (
      <MenuItem
        selected={value === text}
        classes={{
          root: classes.menuItems,
          selected: classes.selectedIndication
        }}
        key={index + 1}
        value={text}
        onClick={() => handleDialogMenuItemClick(text)}
      >
        <div>{text}</div>
      </MenuItem>
    ));
  };

  let menuItems = [
    // this is the mobile modal
    <Dialog
      key={'main_ods_dialog'}
      disableBackdropClick
      open={true}
      aria-labelledby="TESTNOCH"
      aria-describedby="TESTBESCHREIBUNG"
      fullScreen={fullScreen}
    >
      <DialogContent className={classes.modal}>
        <Grid container>
          <Grid item xs={10}>
            <Box pl={2.5} pb={2.5}>
              <Typography variant="h6" className={classes.headline}>
                Bitte wählen Sie eine Indikation
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={2}>
            <Box px={2.5} pb={2.5}>
              <CloseIcon className={classes.closeIconStyle} />
            </Box>
          </Grid>
        </Grid>

        <>
          <div key="other">{renderMobileMenuItems(treatments)}</div>
        </>
      </DialogContent>
    </Dialog>,
    // this is for actual select
    ...treatments.map(({ text }, index) => (
      <MenuItem key={'_' + index + 1} value={text} style={{ display: 'none' }}>
        {text}
      </MenuItem>
    ))
  ];

  if (!mobile) {
    menuItems = [<div key="other">{renderDesktopMenuItems(treatments)}</div>];
  }

  return (
    <FormControl className={classes.textFieldFormControl} fullWidth error={error} required={required} onBlur={onBlur}>
      {label && (
        <InputLabel
          shrink={value !== undefined && value !== ''}
          id={`label-${id}`}
          onClick={e => {
            e.preventDefault();
            handleOpen();
          }}
        >
          {label}
        </InputLabel>
      )}
      <Select
        IconComponent={ExpandMore}
        value={value}
        data-qa-id={'indication-select-input'}
        style={style}
        id={id}
        inputProps={{
          classes: {
            root: classes.selectRoot,
            nativeInput: classes.nativeInput
          }
        }}
        className={className}
        aria-expanded={!!open}
        MenuProps={{
          getContentAnchorEl: null,
          classes: { paper: classes.menuPaper }
        }}
        labelWidth={0}
        name={name}
        fullWidth
        variant="outlined"
        open={open}
        onClose={handleClose}
        onOpen={handleOpen}
      >
        {menuItems}
      </Select>
      {error && errorText ? (
        <FormHelperText
          error={false}
          classes={{
            root: classes.errorFormHelper
          }}
        >
          {errorText}
        </FormHelperText>
      ) : (
        ''
      )}
    </FormControl>
  );
};

export { BasicSelect };
